import {EntryState, EntryStore} from '@stores/entry.store';
import * as APIM from '@common/utils/dist/models/api-messages.js';
import * as PL2 from '@common/utils/dist/models/models-pl2.js';
import {KeyUtilsPL2 as KU} from '@common/utils/dist/utils/key-utils-pl2.js';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';
import {EntryEventChainOutput, EntryEventHandler} from './entry-event-core';

export class AssignmentUpdateHandler implements EntryEventHandler<
  EntryEventChainOutput,
  EntryEventChainOutput
> {
  nextHandler: EntryEventHandler<EntryEventChainOutput, any>;

  constructor(private entryStore: EntryStore) {}

  handle(input: EntryEventChainOutput): EntryEventChainOutput {
    const creates = input.changes[APIM.EventIntent.EntryCreate];
    const deletes = input.changes[APIM.EventIntent.EntryDeepDelete];
    if (creates.length === 0 && deletes.length === 0) {
      return this.nextHandler.handle(input);
    }

    const entryState = this.entryStore.state();
    const assignmentUpdates: {
      [pK: string]: Partial<PL2.AssignmentEntry> & PL2.EntryId;
    } = {};
    const ts = U.timestamp();
    this._addAssignmentUpdates(
      creates,
      APIM.EventIntent.EntryCreate,
      assignmentUpdates,
      entryState,
      ts,
    );
    this._addAssignmentUpdates(
      deletes,
      APIM.EventIntent.EntryDeepDelete,
      assignmentUpdates,
      entryState,
      ts,
    );

    if (U.isEmpty(input.changes[APIM.EventIntent.EntryBatchUpdate])) {
      input.changes[APIM.EventIntent.EntryBatchUpdate] = [];
    }
    input.changes[APIM.EventIntent.EntryBatchUpdate].push(
      ...Object.values(assignmentUpdates),
    );

    return this.nextHandler.handle(input);
  }

  private _isUserEntryAddRemove(e: PL2.AnyPartialEntryWithId): boolean {
    return (
      KU.isType(PL2.EntryType.LabReportUser, e) ||
      KU.isType(PL2.EntryType.InvitedLabReportUser, e)
    );
  }

  private _addAssignmentUpdates(
    entries: Array<PL2.AnyPartialEntryWithId>,
    intent: APIM.EventIntent,
    assignmentUpdates: {[pK: string]: Partial<PL2.AssignmentEntry>},
    entryState: EntryState,
    ts: string,
  ): void {
    entries.forEach((ec) => {
      const pK = KU.stringFromKey(ec);
      const aPK = KU.parentPK(KU.parentPK(pK));
      if (
        !this._isUserEntryAddRemove(ec) ||
        !KU.isType(PL2.EntryType.Assignment, aPK)
      ) {
        return;
      }
      const lrUserEntry = <PL2.LabReportUserEntry>entryState[pK]?.e;
      const aE = <PL2.AssignmentEntry>entryState[aPK]?.e;
      if (!U.isEmpty(aE)) {
        assignmentUpdates[aPK] = assignmentUpdates[aPK] ?? {
          mId: aE.mId,
          sK: aE.sK,
          lmsUIds: U.isEmpty(aE.lmsUIds) ? [] : [...aE.lmsUIds],
          uAt: ts,
        };
        if (intent === APIM.EventIntent.EntryCreate) {
          assignmentUpdates[aPK].lmsUIds.push(lrUserEntry.mUId);
        } else if (intent === APIM.EventIntent.EntryDeepDelete) {
          assignmentUpdates[aPK].lmsUIds = assignmentUpdates[
            aPK
          ].lmsUIds.filter((lmsUId) => lmsUId !== lrUserEntry.mUId);
        }
      }
    });
  }
}
